import {Component, computed, effect, ElementRef, OnInit, signal, viewChild} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CodaltComponent} from '../codalt.component';
import {Checkpoint} from '../classes/checkpoint';
import {CheckpointService} from '../services/checkpoint.service';
import {CheckpointResult} from '../classes/checkpoint-result';
import {DatePipe, formatDate} from '@angular/common';
import {PlatformService} from '../services/platform.service';
import {Platform} from '../classes/platform';
import {CheckpointResultService} from '../services/checkpoint-result.service';
import {Routenames} from '../classes/route-name.enum';
import {DurationPipe} from '../pipes/duration.pipe';
import {
    CheckpointHeaderComponent,
    dayTime,
    resultProblem,
    resultStatus
} from '../services/checkpoint-header/checkpoint-header.component';
import {BytesPipePipe} from '../pipes/bytes.pipe';
import {MsBetweenPipe} from '../pipes/ms-between.pipe';
import {User} from '../classes/user.class';
import {CheckpointComment} from '../classes/checkpoint-comment';
import {CheckpointCommentService} from '../services/checkpoint-comment.service';
import {CodaltDatePipe} from '../pipes/codalt-date.pipe';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatHint, MatSuffix} from '@angular/material/form-field';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Utils} from '../utils.class';
import {ConfirmDialogService} from '../services/confirm-dialog-service/confirm-dialog.service';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {Title} from '@angular/platform-browser';
import {browserTitle} from '../page-title.strategy';
import {Subscription} from 'rxjs';
import {FieldErrorComponent} from '../field-error/field-error.component';
import {
    BreadcrumbsComponent,
    CheckStripComponent,
    CheckStripItem,
    DataListComponent,
    DataRowComponent,
    DayGroupComponent,
    EmptyStateComponent,
    FieldComponent,
    FilterChipsComponent,
    FilterOption,
    InfoBannerComponent,
    PageComponent,
    relativeDay,
    SectionComponent,
    StatusBadgeComponent,
    StatusDotComponent
} from '../shared/ui';

type DurationChoice = number | 'other';

/** Number of results in the strip above the list. */
const STRIP_LENGTH = 60;

@Component({
    selector: 'app-checkpoint-overview',
    templateUrl: './checkpoint-overview.component.html',
    styleUrls: ['./checkpoint-overview.component.scss'],
    imports: [
        DatePipe,
        RouterLink,
        DurationPipe,
        CheckpointHeaderComponent,
        BytesPipePipe,
        MsBetweenPipe,
        CodaltDatePipe,
        MatFormField,
        MatHint,
        MatSuffix,
        MatInput,
        FormsModule,
        ReactiveFormsModule,
        FieldErrorComponent,
        MatButton,
        MatIconButton,
        MatTooltip,
        BreadcrumbsComponent,
        CheckStripComponent,
        DataListComponent,
        DataRowComponent,
        DayGroupComponent,
        EmptyStateComponent,
        FieldComponent,
        FilterChipsComponent,
        InfoBannerComponent,
        PageComponent,
        SectionComponent,
        StatusBadgeComponent,
        StatusDotComponent,
    ],
})
export class CheckpointOverviewComponent extends CodaltComponent implements OnInit {
    readonly platform = signal<Platform>(new Platform());
    readonly checkpoint = signal<Checkpoint>(new Checkpoint());
    readonly checkpointResults = signal<null | CheckpointResult[]>(null);
    readonly checkpointComments = signal<null | CheckpointComment[]>(null);

    users: User[] = [];
    platformId: number;

    readonly successfulPush = signal<boolean>(false);
    readonly failedPush = signal<boolean>(false);

    readonly formCreateComment = signal<null | FormGroup>(null);

    // Subscriptions of the current route params, replaced when navigating to another checkpoint
    private routeSubscriptions = new Subscription();

    readonly isPostponed = computed(() => {
        const postponedTill = this.checkpoint()?.postponed_till;
        return !!postponedTill && new Date(postponedTill) > new Date();
    });

    readonly postponedTill = computed(() => dayTime(this.checkpoint()?.postponed_till).toLowerCase());

    /** The latest comment set the current postponement, so its author is who postponed. */
    readonly postponedBy = computed(() => {
        if (!this.isPostponed()) {
            return null;
        }
        const latest = [...(this.checkpointComments() ?? [])]
            .filter(comment => comment)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        return latest?.postpone_hours ? latest.user?.name ?? null : null;
    });

    /** Results grouped per day (newest first) with their display status. */
    readonly resultGroups = computed(() => {
        const groups: {day: string, rows: {result: CheckpointResult, status: ReturnType<typeof resultStatus>, problem: string}[]}[] = [];
        for (const result of this.checkpointResults() ?? []) {
            const date = result.finish_date ?? result.start_date;
            const relative = relativeDay(date);
            const fullDate = date ? formatDate(date, 'EEE d MMM yyyy', 'nl') : '';
            const day = ['Vandaag', 'Gisteren'].includes(relative) ? `${relative} · ${fullDate}` : fullDate;
            if (groups[groups.length - 1]?.day !== day) {
                groups.push({day, rows: []});
            }
            groups[groups.length - 1].rows.push({
                result,
                status: resultStatus(result),
                problem: resultProblem(result, this.checkpoint())
            });
        }
        return groups;
    });

    /** The last results as colored bars, oldest left. */
    readonly strip = computed<CheckStripItem[]>(() => (this.checkpointResults() ?? [])
        .slice(0, STRIP_LENGTH)
        .reverse()
        .map(result => {
            const status = resultStatus(result);
            const date = result.finish_date ?? result.start_date;
            return {
                tone: status.tone === 'neutral' ? 'none' : status.tone as CheckStripItem['tone'],
                title: `${date ? formatDate(date, 'd MMM HH:mm', 'nl') : ''} · ${status.label}`
            };
        }));

    readonly stripSummary = computed(() => {
        const results = (this.checkpointResults() ?? []).slice(0, STRIP_LENGTH);
        const finished = results.filter(result => result.finish_date);
        if (!results.length) {
            return null;
        }
        const good = finished.filter(result => !result.error && !result.warning).length;
        const percentage = finished.length ? Math.round(good / finished.length * 100) : 0;
        return `Laatste ${results.length} checks · ${percentage}% goed`;
    });

    readonly durationOptions: FilterOption<DurationChoice>[] = [
        {value: 1, label: '1 uur'},
        {value: 4, label: '4 uur'},
        {value: 24, label: '24 uur'},
        {value: 'other', label: 'Anders…'},
    ];
    readonly durationChoice = signal<DurationChoice | null>(null);
    readonly newComment = signal('');
    private readonly newCommentInput = viewChild<ElementRef<HTMLTextAreaElement>>('newCommentInput');

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointCommentService: CheckpointCommentService,
        private checkpointResultService: CheckpointResultService,
        private checkpointService: CheckpointService,
        private confirmDialog: ConfirmDialogService,
        private platformService: PlatformService,
        private titleService: Title,
    ) {
        super();
        // A new (empty) comment form after saving also clears the chosen duration and the comment draft
        effect(() => {
            this.formCreateComment();
            this.durationChoice.set(null);
            this.newComment.set('');
        });
    }

    ngOnInit() {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { platformId; checkId }) => {
                // Until the platform is loaded and its name is known
                this.titleService.setTitle(browserTitle('Seire'));
                this.platformId = +params.platformId;
                this.routeSubscriptions.unsubscribe();
                this.routeSubscriptions = new Subscription();
                this.subscriptions.add(this.routeSubscriptions);
                this.checkpointResults.set(null);

                this.routeSubscriptions.add(
                    this.platformService
                        .liveItem(
                            +params.platformId,
                            this.platformService.getPlatform(+params.platformId),
                        )
                        .subscribe({
                            next: (platform) => {
                                this.platform.set(platform);
                                this.setCrumbs();
                                this.titleService.setTitle(browserTitle(`${this.platform().name} - Seire`));
                            },
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointService
                        .liveItem(
                            +params.checkId,
                            this.checkpointService.getCheckpoint(+params.checkId),
                        )
                        .subscribe({
                            next: (checkpoint) => {
                                this.checkpoint.set(checkpoint);
                                this.setCrumbs();
                            },
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointResultService
                        .liveList(
                            this.checkpointResultService.getCheckpointResults(+params.checkId),
                            (checkpointResult) =>
                                +checkpointResult.platform_check_id === +params.checkId,
                        )
                        .subscribe({
                            // Pushed results are appended, newest first matches the order of the API
                            next: (checkpointResults) =>
                                this.checkpointResults.set(
                                    [...checkpointResults].sort((a, b) => b.id - a.id),
                                ),
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointCommentService.getCheckpointComments(+params.checkId).subscribe({
                        next: (checkpointComments) => {
                            this.checkpointComments.set(checkpointComments);
                        },
                        error: (err) => console.error(err),
                    }),
                );

                this.createForm(+params.checkId);
            }),
        );
    }

    setCrumbs() {
        this.crumbs = [
            { name: 'Seire', route: Routenames.seire },
            {
                name: `Platform : ${this.platform().name}`,
                route: `${Routenames.seire}/${this.platform().id}`,
            },
            {
                name: `Checkpoint : ${this.checkpoint().name}`,
                route: `${Routenames.seire}/${this.platform().id}/overview/${this.checkpoint().id}`,
            },
        ];
    }

    createForm(checkpointId: number) {
        const checkpointComment = new CheckpointComment();

        this.formCreateComment.set(
            new FormGroup({
                id: new FormControl(checkpointComment.id),
                checkpoint_id: new FormControl(checkpointId),
                postpone_hours: new FormControl(
                    checkpointComment.postpone_hours,
                    Validators.max(72),
                ),
                comment: new FormControl(checkpointComment.comment, Validators.required),
                updated_at: new FormControl(checkpointComment.updated_at),
            }),
        );
    }

    save(providedForm: FormGroup) {
        Utils.triggerValidation(providedForm);
        if (providedForm.valid) {
            const checkpointComment = new CheckpointComment();
            Object.assign(checkpointComment, providedForm.value);

            this.subscriptions.add(
                this.checkpointCommentService.saveCheckpointComment(checkpointComment).subscribe({
                    next: (savedComment) => {
                        this.checkpointComments.set([
                            savedComment?.data,
                            ...this.checkpointComments(),
                        ]);
                        this.createForm(this.checkpoint().id);
                    },
                    error: (err) => console.error(err),
                }),
            );
        }
    }

    /** Duration chips fill the existing postpone_hours field; "Anders…" shows that field itself. */
    chooseDuration(choice: DurationChoice | null) {
        this.durationChoice.set(choice);
        if (typeof choice === 'number') {
            this.formCreateComment()?.get('postpone_hours').setValue(choice);
        }
    }

    /** A comment without postponement goes through the same form; as before, this lifts a postponement. */
    placeComment() {
        const form = this.formCreateComment();
        if (!form || !this.newComment().trim()) {
            return;
        }
        form.patchValue({comment: this.newComment(), postpone_hours: null});
        this.save(form);
    }

    focusComment() {
        const input = this.newCommentInput()?.nativeElement;
        input?.scrollIntoView({block: 'center', behavior: 'smooth'});
        input?.focus({preventScroll: true});
    }

    deleteComment(event: PointerEvent, providedCheckpointComment: CheckpointComment) {
        event.preventDefault();
        event.stopPropagation();

        const checkpointComment = new CheckpointComment();
        Object.assign(checkpointComment, providedCheckpointComment);

        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet u zeker dat u de opmerking wilt verwijderen?`,
                'Verwijderen',
                'Annuleren',
            )
            .then(() => {
                this.checkpointCommentService
                    .deleteCheckpointComment(checkpointComment.id)
                    .then(() => {
                        this.checkpointComments.update((checkpointComments) =>
                            checkpointComments.filter(
                                (comment) => comment.id !== checkpointComment.id,
                            ),
                        );
                    });
            })
            .catch(() => {
                console.log('checkpoint comment not deleted');
            });
    }

    queueImmediately() {
        this.subscriptions.add(
            this.checkpointService.queueImmediately(this.checkpoint().id).subscribe({
                next: (removedCheckpointResult) => {
                    this.checkpointResults.update((checkpointResults) =>
                        checkpointResults.filter(
                            (checkpointResult) =>
                                checkpointResult.id !== removedCheckpointResult.data.id,
                        ),
                    );
                    this.successfulPush.set(true);
                    setTimeout(() => {
                        this.successfulPush.set(false);
                    }, 2500);
                },
                error: (err) => {
                    console.error(err);
                    this.failedPush.set(true);
                    setTimeout(() => {
                        this.failedPush.set(false);
                    }, 2500);
                },
            }),
        );
    }
}
